import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { AlertTriangle, Edit } from 'react-feather';
import { useRemote } from '../../Utils/Utils';
import ComparePane from './ComparePane';
import CompareCentreRail from './CompareCentreRail';
import CompareCommentsPanel from './CompareCommentsPanel';
import CompareHeader from './CompareHeader';
import diffContentMap from './diff/diffContentMap';
import diffMarksAddin from './diff/diffMarksAddin';
import { authorForRef, editorPathForDocument, fetchCompareOptions, fetchContentForRef, defaultRefs, isLegacyOrientation, parseRef } from './versionApi';
import { collectDiffableKeys, collectPages, currentPageIndex, hiddenChangedKeys, humaniseDataName, mapScrollPosition, readPageGeometry, readPageTops, readSegmentSpans, segmentSpans, syncScrollTop } from './paneContent';
import { watchScrollSettle } from './scrollSettle';
import createSideLoader from './sideLoader';
import './ComparePage.scss';

// Compare Documents container (RES-561): /compare/:ticker/:template?left=<ref>&right=<ref>
// ticker is Base64 (same encoding the /versions endpoints use); refs are deep-linkable
// (see versionApi.parseRef). Renders both versions side by side with the diff
// materialised as leaf marks (red strikethrough left / red highlight right), per-pane
// show/hide-changes toggles, and a centre rail with sync-scroll and page navigation.

// Comments are hidden for now (Chris's 2026-08-20 feedback): he wants the left/right
// comments back once they can be pinned to specific text, so the code stays. Flipping
// this to true restores the panel, the per-pane comment icons, and the rail's
// Show-comments button.
const COMPARE_COMMENTS_ENABLED = false;

const ComparePage = () => {
	const { ticker: b64Ticker, template: rawTemplate } = useParams();
	const location = useLocation();
	const history = useHistory();
	const remote = useRemote();

	// Same v5 quirk as the ticker: a template name with a space arrives as %20 on a
	// cold reload; entry points build the path with encodeURIComponent (comparePath).
	const templateName = useMemo(() => {
		try {
			return decodeURIComponent(rawTemplate);
		} catch (e) {
			return rawTemplate;
		}
	}, [rawTemplate]);

	const ticker = useMemo(() => {
		try {
			// React Router v5 does not decode path params, so Base64 padding arrives
			// as %3D; decodeURIComponent is a no-op when the value is already plain.
			return atob(decodeURIComponent(b64Ticker));
		} catch (e) {
			return null;
		}
	}, [b64Ticker]);
	const tickers = useMemo(() => (ticker ? ticker.split(',') : []), [ticker]);
	// Canonical Base64 for API calls, independent of how the route param was encoded.
	const b64 = useMemo(() => (ticker ? btoa(ticker) : null), [ticker]);

	const [templateConfig, setTemplateConfig] = useState(null);
	// The UNEXPANDED template, for the diff key set only (see the `diff` memo).
	// Tri-state: undefined = the fetch hasn't settled, null = it failed (fall back to
	// the expanded config), object = loaded. The diff waits for it to settle so it is
	// computed exactly once — a second computation would re-annotate content the Raw
	// editors have already mounted with, and paneKeyGen wouldn't remount them.
	const [fullTemplate, setFullTemplate] = useState(undefined);
	const [optionsState, setOptionsState] = useState(null);
	// Offer number backing the header's editor deep link for FUND-type templates
	// (item 8): the compare tickerKey is the fund number alone, but only ?offer=
	// selects the editor's scheme dropdown. Tri-state: undefined = resolution in
	// flight (the link waits — a click mid-fetch must not mint the degraded form,
	// review finding), null = RESOLVED without an offer (badged funds aren't in the
	// offers list) → the link degrades to ?fund= only.
	const [fundOffer, setFundOffer] = useState(null);
	const [leftContent, setLeftContent] = useState(null);
	const [rightContent, setRightContent] = useState(null);
	const [fatalError, setFatalError] = useState(ticker ? null : 'This compare link is invalid');
	const [showChanges, setShowChanges] = useState({ left: true, right: true });
	const [syncScroll, setSyncScroll] = useState(true);
	// Which template page the panes are on (0-based) — scroll-spied from the pane
	// bodies and set directly by the rail's page buttons.
	const [pageIndex, setPageIndex] = useState(0);
	// Comments panel (plan §4): opened from a pane's comment icon (preselecting that
	// side) or the centre rail (keeps the last side, defaulting to left — the newer
	// version since follow-up item 3). side is which pane's version is being viewed.
	const [commentsPanel, setCommentsPanel] = useState({ open: false, side: 'left' });

	const leftBodyRef = useRef(null);
	const rightBodyRef = useRef(null);
	// True while a scroll event is the echo of a programmatic scrollTop write.
	const scrollEchoRef = useRef(false);
	// True while a page-navigation jump is animating (both panes scroll at once).
	const jumpGuardRef = useRef(false);
	// Cancels the settle-watcher of the jump in flight (see scrollSettle.js).
	const jumpSettleCancelRef = useRef(null);
	// Stale-response + purged-ref-loop guards for the per-side content loads.
	// Lazy init: one instance for the component's whole life — the document-change
	// effect calls reset() on it rather than replacing it, so responses in flight
	// for the previous document invalidate properly.
	const sideLoaderRef = useRef(null);
	if (!sideLoaderRef.current)
		sideLoaderRef.current = createSideLoader();
	// Bumped when the document (ticker/template) changes, so an options response
	// fetched for the previous document can never land on this one.
	const optionsGenRef = useRef(0);
	// One-shot flag per document: legacy-orientation URL normalisation must run
	// exactly once on arrival, or it would fight later manual dropdown choices.
	const legacyNormalisedRef = useRef(false);

	const query = new URLSearchParams(location.search);
	const leftRef = query.get('left');
	const rightRef = query.get('right');

	const setRef = (side, value) => {
		const params = new URLSearchParams(location.search);
		params.set(side, value);
		// state carries the library's doc list for header Prev/Next — keep it.
		history.replace({ pathname: location.pathname, search: '?' + params.toString(), state: location.state });
	};

	const refreshOptions = () => {
		const gen = optionsGenRef.current;
		return fetchCompareOptions(remote, { b64Ticker: b64, templateName, ticker }).then((opts) => {
			if (gen === optionsGenRef.current)
				setOptionsState(opts);
		});
	};

	// The header's Previous/Next Doc buttons navigate this mounted component to a
	// DIFFERENT document, so everything document-scoped resets here: template config
	// and options (a late response for the old document must not land on the new
	// one), any fatal error, and the side loader (in-flight loads invalidated, the
	// purged cache cleared — see sideLoader.reset).
	useEffect(() => {
		optionsGenRef.current++;
		legacyNormalisedRef.current = false;
		sideLoaderRef.current.reset();
		setTemplateConfig(null);
		setFullTemplate(undefined);
		setOptionsState(null);
		setLeftContent(null);
		setRightContent(null);
		setPageIndex(0);
		setFatalError(ticker ? null : 'This compare link is invalid');
		if (!ticker)
			return undefined;
		let stale = false;
		remote.get(`/data/templates/${encodeURIComponent(templateName)}/page-expansion?ticker=${encodeURIComponent(tickers[0])}`)
			.then((config) => {
				if (!stale)
					setTemplateConfig(config);
			})
			.catch(() => {
				if (!stale)
					setFatalError('Unable to load the template configuration for this document');
			});
		// The unexpanded template — same document, no ticker, so no conditional page or
		// block has been stripped from it. Failure is NOT fatal: the diff falls back to
		// the expanded key set, which is exactly the pre-fix behaviour.
		remote.get(`/data/templates/${encodeURIComponent(templateName)}`)
			.then((full) => {
				if (!stale)
					setFullTemplate(full || null);
			})
			.catch(() => {
				if (!stale)
					setFullTemplate(null);
			});
		refreshOptions();
		return () => {
			stale = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [b64Ticker, templateName]);

	useEffect(() => () => {
		if (jumpSettleCancelRef.current)
			jumpSettleCancelRef.current();
	}, []);

	// Resolve the fund's offer for the editor deep link (fund-type templates only)
	// from the same list the editor sidebar loads. Keyed on fullTemplate, so it also
	// re-runs (and resets) with the document-scoped state on Prev/Next Doc.
	useEffect(() => {
		const fundType = fullTemplate?.ticker?.type === 'fund' && !!tickers[0];
		setFundOffer(fundType ? undefined : null);
		if (!fundType)
			return undefined;
		let stale = false;
		remote.get('/funds/offers?excludeBadged=true')
			.then((offers) => {
				if (stale)
					return;
				const offer = (offers || []).find((o) => (o.funds || []).some((f) => f.fundNumber === tickers[0]));
				setFundOffer(offer ? offer.offerNumber : null);
			})
			.catch(() => {
				// Resolved-without-offer: the degraded ?fund= link is better than a
				// link that never appears.
				if (!stale)
					setFundOffer(null);
			});
		return () => {
			stale = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fullTemplate, tickers]);

	// Once the version lists are known: normalise legacy-orientation links, then
	// fill in missing refs.
	useEffect(() => {
		if (!optionsState)
			return;
		// Links minted before the item-3 swap carry the NEWER doc on the right
		// (?left=<older>&right=current / ?left=pub:<older>&right=pub:<newer>) and
		// would render every diff mark inverted. Swap them once on arrival; the
		// dropdowns can still set any pairing afterwards. (A post-swap link the
		// sharer deliberately inverted matches the same shapes and is normalised
		// too — accepted, the legacy links are the overwhelming case.)
		if (!legacyNormalisedRef.current) {
			legacyNormalisedRef.current = true;
			if (leftRef && rightRef && isLegacyOrientation(leftRef, rightRef, optionsState.published)) {
				const params = new URLSearchParams(location.search);
				params.set('left', rightRef);
				params.set('right', leftRef);
				history.replace({ pathname: location.pathname, search: '?' + params.toString(), state: location.state });
				return;
			}
		}
		if (leftRef && rightRef)
			return;
		const defaults = defaultRefs(optionsState.options);
		const params = new URLSearchParams(location.search);
		if (!params.get('left'))
			params.set('left', defaults.left);
		if (!params.get('right') && defaults.right)
			params.set('right', defaults.right);
		history.replace({ pathname: location.pathname, search: '?' + params.toString(), state: location.state });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [optionsState, leftRef, rightRef]);

	useEffect(() => {
		loadSide('left', leftRef, setLeftContent);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leftRef, optionsState]);

	useEffect(() => {
		loadSide('right', rightRef, setRightContent);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rightRef, optionsState]);

	function loadSide(side, refString, setContent) {
		if (!refString || !optionsState) {
			setContent(null);
			return;
		}
		// The loader drops responses a newer load for this side has overtaken, and
		// caches purged refs so the options refresh (which re-runs these effects)
		// can't loop: the purged draft disappears from the dropdown exactly once.
		sideLoaderRef.current(
			side,
			refString,
			() => fetchContentForRef(remote, refString, { b64Ticker: b64, templateName, versions: optionsState.versions }),
			setContent,
			refreshOptions
		);
	}

	// Header context: the library entry point hands over its doc list + focal index in
	// location.state; otherwise the focal document is whichever pane holds a published
	// doc (left first — it's the newer side since follow-up item 3).
	const { compareDocs, compareIndex } = location.state || {};
	const focalDoc = useMemo(() => {
		const matchesRef = (doc, refString) => {
			const ref = parseRef(refString);
			return !!(doc && ref && ref.kind === 'pub' && doc.docId === ref.id);
		};
		// The library's doc only names the header while a pane still shows it — after
		// a manual dropdown change, re-derive from the live refs instead.
		const stateDoc = compareDocs && compareDocs[compareIndex];
		if (stateDoc && (matchesRef(stateDoc, rightRef) || matchesRef(stateDoc, leftRef)))
			return stateDoc;
		const published = optionsState ? optionsState.published : [];
		const refToDoc = (refString) => {
			const ref = parseRef(refString);
			return (ref && ref.kind === 'pub' && published.find((d) => d.docId === ref.id)) || null;
		};
		return refToDoc(leftRef) || refToDoc(rightRef);
	}, [compareDocs, compareIndex, optionsState, leftRef, rightRef]);

	// The header's author follows the LEFT pane's version (2026-08-17 feedback
	// item 5) — focalDoc can be the RIGHT pane's published doc when the left holds
	// the current draft, and its author mislabelled the draft comparison.
	const headerAuthor = useMemo(
		() => (optionsState ? authorForRef(leftRef, optionsState) : null),
		[leftRef, optionsState]
	);

	// The left pane's editor deep link (item 8; moved from the page header beside the
	// Current-draft selector, 2026-08-21 feedback) — derived from the CURRENT route params +
	// unexpanded template, so it stays correct across Prev/Next Doc and on cold deep
	// links. No link until fullTemplate SETTLES (undefined = pending): minting early
	// would hand a click in that window a template-only link for a document the
	// settled config can fully resolve (review finding). A FAILED fetch (null) still
	// mints the degraded, template-only link.
	const editorLink = useMemo(() => {
		const tickerConf = fullTemplate?.ticker;
		// Fund-type also waits out the offer resolution (undefined = in flight).
		if (fullTemplate === undefined || (tickerConf?.type === 'fund' && fundOffer === undefined))
			return null;
		return editorPathForDocument({
			templateName,
			tickerType: tickerConf?.type || null,
			tickerKey: ticker,
			offerNumber: fundOffer,
			documentSelectionOptions: tickerConf?.documentSelectionOptions || null,
		});
	}, [templateName, fullTemplate, ticker, fundOffer]);

	// The two-sided diff, restricted to the keys the panes render through a Raw Slate
	// component — hunks in any other key could never be displayed.
	// diffContentMap's arg order is (old, new): additions annotate its second arg.
	// The LEFT pane is the newer side (follow-up item 3), so it feeds the "new" slot
	// — paired with resolveBlockValue's side→annotated mapping in paneContent.js;
	// change both together or every deletion renders as an addition.
	//
	// The keys come from the UNEXPANDED template, not the expanded `templateConfig`:
	// page-expansion strips conditional pages/blocks against CURRENT fund data, so
	// diffing only what survives means a change on a stripped page is never computed
	// and vanishes without trace (defect 1 of the 2026-08-07 CFS Investments run).
	// Diffing the full key set keeps it computed; hiddenChanged (below) surfaces it.
	const diff = useMemo(() => {
		if (!templateConfig || fullTemplate === undefined || !leftContent || leftContent.status !== 'ok' || !rightContent || rightContent.status !== 'ok')
			return null;
		return diffContentMap(rightContent.data, leftContent.data, collectDiffableKeys(fullTemplate || templateConfig));
	}, [templateConfig, fullTemplate, leftContent, rightContent]);

	// One shared addin instance: the panes remount their editors per diff generation
	// (paneKeyGen), so a stable array avoids needless extra remounts.
	const compareAddins = useMemo(() => [diffMarksAddin()], []);
	const paneKeyGen = leftRef + '|' + rightRef + '|' + (diff ? 'diff' : 'plain');

	// Per-side page lists (item 6): each pane's flow pages split at that side's
	// stored page breaks, so the sides can have different counts. Computed once
	// here and passed into the panes (the parse of a large flow body isn't free —
	// don't repeat it per consumer).
	const sidePages = (content) =>
		(templateConfig ? collectPages(templateConfig, content && content.status === 'ok' ? content.data : null) : []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const leftPages = useMemo(() => sidePages(leftContent), [templateConfig, leftContent]);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const rightPages = useMemo(() => sidePages(rightContent), [templateConfig, rightContent]);

	// Changed keys with no block to display them in either pane — see hiddenChangedKeys.
	const hiddenChanged = useMemo(() => hiddenChangedKeys(diff, leftPages, rightPages), [diff, leftPages, rightPages]);

	// Rail counter shows the LARGER side; jumpToPage clamps per pane (a pane
	// without the target wrapper simply doesn't scroll). Floors at 1 while the
	// template/content load so the rail can always render "n / N".
	const pageCount = Math.max(1, leftPages.length, rightPages.length);

	// pageCount is content-dependent, so a same-document version switch can
	// SHRINK it below the current index (the scroll-spy would only correct on
	// the next scroll) — clamp eagerly so the counter never reads past its max.
	useEffect(() => {
		setPageIndex((i) => Math.min(i, pageCount - 1));
	}, [pageCount]);

	const makeScrollHandler = (sourceRef, targetRef) => () => {
		if (jumpGuardRef.current)
			return;
		if (scrollEchoRef.current) {
			scrollEchoRef.current = false;
			return;
		}
		if (!sourceRef.current)
			return;
		// One geometry read serves both the spy and the sync (the wrappers were
		// measured twice per event — review finding). Re-read per event: page
		// geometry can change under the diff toggles.
		const sourceGeometry = readPageGeometry(sourceRef.current);
		// Scroll-spy the page counter even with sync OFF — the last-scrolled pane
		// wins (identical to the synced pane when sync is on).
		setPageIndex(currentPageIndex(sourceRef.current, sourceGeometry.map((page) => page.top)));
		if (!syncScroll || !targetRef.current)
			return;
		// Segment-anchored sync: panes re-align at every template-page boundary
		// instead of drifting whenever their total heights differ
		// (plan-compareDocuments.md §3.12 C).
		scrollEchoRef.current = syncScrollTop(
			sourceRef.current,
			targetRef.current,
			segmentSpans(sourceGeometry, sourceRef.current.scrollHeight),
			readSegmentSpans(targetRef.current)
		);
	};

	// The rail's page buttons: the rail's index space is the LARGER side's render
	// pages (pageCount), so that pane leads the jump and the other follows through
	// the same template-anchored map the sync uses. Landing both panes on the same
	// render index put them on DIFFERENT template pages when flow splits diverge,
	// and the first post-jump scroll visibly snapped the follower as the sync
	// corrected the landing (review finding). The counter is set at click time;
	// the guard suppresses spy + sync while the smooth scrolls animate. The guard is
	// released on scroll-SETTLE, not a fixed timer — a timer that expired before the
	// longer pane's animation finished let the tail re-enter the sync handler and
	// drag the shorter pane to its bottom (defect 2, 2026-08-05 Simplicity run).
	const jumpToPage = (nextIdx) => {
		const target = Math.min(Math.max(nextIdx, 0), pageCount - 1);
		setPageIndex(target);
		jumpGuardRef.current = true;
		const leftLeads = leftPages.length >= rightPages.length;
		const leadBody = (leftLeads ? leftBodyRef : rightBodyRef).current;
		const followBody = (leftLeads ? rightBodyRef : leftBodyRef).current;
		if (leadBody) {
			const top = readPageTops(leadBody)[target];
			if (top !== undefined) {
				const leadRange = leadBody.scrollHeight - leadBody.clientHeight;
				const leadTop = Math.max(0, Math.min(top, leadRange));
				leadBody.scrollTo({ top: leadTop, behavior: 'smooth' });
				if (followBody) {
					const followRange = followBody.scrollHeight - followBody.clientHeight;
					const mapped = mapScrollPosition(leadTop, readSegmentSpans(leadBody), readSegmentSpans(followBody));
					// No spans (unstamped wrappers) → the old per-side landing.
					let followTop = mapped !== null ? mapped : readPageTops(followBody)[target];
					// A lead pinned at its bottom clamp takes the follower to ITS
					// bottom — same end-together rule as syncScrollTop.
					if (leadTop >= leadRange - 1)
						followTop = followRange;
					if (followTop !== undefined)
						followBody.scrollTo({ top: Math.max(0, Math.min(followTop, followRange)), behavior: 'smooth' });
				}
			}
		}
		if (jumpSettleCancelRef.current)
			jumpSettleCancelRef.current();
		jumpSettleCancelRef.current = watchScrollSettle(
			() => [leftBodyRef, rightBodyRef].map((ref) => (ref.current ? ref.current.scrollTop : 0)),
			() => {
				jumpGuardRef.current = false;
			}
		);
	};

	const openCommentsFor = (side) =>
		setCommentsPanel((s) => (s.open && s.side === side ? { ...s, open: false } : { open: true, side }));

	if (fatalError) {
		return (
			<div className="compare-page">
				<div className="compare-page-error">{fatalError}</div>
			</div>
		);
	}

	const options = optionsState ? optionsState.options : [];
	// The nullable default side is RIGHT since item 3 (left is always 'current'):
	// no right ref in the URL and nothing to default it to = nothing to compare.
	const nothingToCompare = optionsState && !rightRef && !defaultRefs(options).right;

	return (
		<div
			className="compare-page"
			data-changed-keys={diff ? Object.values(diff.keys).filter((k) => k.status !== 'unchanged').length : undefined}
			data-hidden-changed-keys={hiddenChanged.length ? hiddenChanged.length : undefined}
		>
			<CompareHeader
				templateName={templateName}
				ticker={ticker}
				focalDoc={focalDoc}
				author={headerAuthor}
				navDocs={compareDocs}
				navIndex={compareIndex}
			/>
			{hiddenChanged.length > 0 && (
				// A change exists that this layout has no page for. Never drop it
				// silently — the reviewer would count the marks and believe that is all
				// of them (2026-08-07 run, defect 1 / Open question 2).
				<div className="compare-page-hidden-changes" role="status">
					<AlertTriangle size={18} className="compare-page-hidden-changes-icon" />
					<span>
						<strong>
							{hiddenChanged.length === 1
								? '1 change isn’t shown below.'
								: `${hiddenChanged.length} changes aren’t shown below.`}
						</strong>
						{' '}
						{hiddenChanged.map(humaniseDataName).join(', ')}
						{hiddenChanged.length === 1 ? ' changed between these versions, but the page that displays it isn’t' : ' changed between these versions, but the pages that display them aren’t'}
						{' part of the current document layout.'}
					</span>
				</div>
			)}
			{nothingToCompare ? (
				<div className="compare-page-empty">
					<p>This document has no published versions or saved drafts to compare yet.</p>
					{editorLink && (
						// The panes (and their editor link) don't render here, but this
						// dead end is where the way back to the editor matters most
						// (2026-08-21 relocation review finding).
						<button type="button" className="compare-page-empty-editor" onClick={() => history.push(editorLink)}>
							<Edit size={16} />
							Open in Editor
						</button>
					)}
				</div>
			) : (
				<div className={'compare-page-grid' + (commentsPanel.open ? ' compare-page-grid-comments' : '')}>
					<ComparePane
						side="left"
						refValue={leftRef}
						options={options}
						onRefChange={(value) => setRef('left', value)}
						content={leftContent}
						pages={leftPages}
						templateConfig={templateConfig}
						templateName={templateName}
						tickers={tickers}
						diff={diff}
						additionalAddins={compareAddins}
						showChanges={showChanges.left}
						onToggleChanges={() => setShowChanges((s) => ({ ...s, left: !s.left }))}
						bodyRef={leftBodyRef}
						onBodyScroll={makeScrollHandler(leftBodyRef, rightBodyRef)}
						paneKeyGen={paneKeyGen}
						onOpenComments={COMPARE_COMMENTS_ENABLED ? () => openCommentsFor('left') : undefined}
						commentsSelected={commentsPanel.open && commentsPanel.side === 'left'}
						editorLink={editorLink}
					/>
					{COMPARE_COMMENTS_ENABLED && commentsPanel.open ? (
						<CompareCommentsPanel
							side={commentsPanel.side}
							onSideChange={(side) => setCommentsPanel((s) => ({ ...s, side }))}
							onClose={() => setCommentsPanel((s) => ({ ...s, open: false }))}
							ticker={ticker}
							templateName={templateName}
							leftRef={leftRef}
							rightRef={rightRef}
							options={options}
							rail={(
								<CompareCentreRail
									horizontal
									pageCount={pageCount}
									pageIndex={pageIndex}
									onFirstPage={() => jumpToPage(0)}
									onPrevPage={() => jumpToPage(pageIndex - 1)}
									onNextPage={() => jumpToPage(pageIndex + 1)}
									onLastPage={() => jumpToPage(pageCount - 1)}
									syncScroll={syncScroll}
									onToggleSync={() => setSyncScroll((s) => !s)}
								/>
							)}
						/>
					) : (
						<CompareCentreRail
							pageCount={pageCount}
							pageIndex={pageIndex}
							onFirstPage={() => jumpToPage(0)}
							onPrevPage={() => jumpToPage(pageIndex - 1)}
							onNextPage={() => jumpToPage(pageIndex + 1)}
							onLastPage={() => jumpToPage(pageCount - 1)}
							syncScroll={syncScroll}
							onToggleSync={() => setSyncScroll((s) => !s)}
							onToggleComments={COMPARE_COMMENTS_ENABLED ? () => setCommentsPanel((s) => ({ ...s, open: true })) : undefined}
						/>
					)}
					<ComparePane
						side="right"
						refValue={rightRef}
						options={options}
						onRefChange={(value) => setRef('right', value)}
						content={rightContent}
						pages={rightPages}
						templateConfig={templateConfig}
						templateName={templateName}
						tickers={tickers}
						diff={diff}
						additionalAddins={compareAddins}
						showChanges={showChanges.right}
						onToggleChanges={() => setShowChanges((s) => ({ ...s, right: !s.right }))}
						bodyRef={rightBodyRef}
						onBodyScroll={makeScrollHandler(rightBodyRef, leftBodyRef)}
						paneKeyGen={paneKeyGen}
						onOpenComments={COMPARE_COMMENTS_ENABLED ? () => openCommentsFor('right') : undefined}
						commentsSelected={commentsPanel.open && commentsPanel.side === 'right'}
					/>
				</div>
			)}
		</div>
	);
};

export default ComparePage;
